import React, { useContext, useEffect, useRef, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import { Skeleton, styled } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { enqueueSnackbar } from 'notistack';

import BiteDBAPI from 'api/BiteDBAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  DatabaseEmailAudienceEditorProps,
  DatabaseEmailAudienceView,
} from './databaseEmailAudience.types';

const MAX_RECIPIENTS = 50;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  '.MuiOutlinedInput-input': {
    minHeight: '48px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Colors-Border-border-primary, var(--bg-border-db))',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  minHeight: '48px',
  background: 'var(--bg-primary)',
  width: '100%',
});

const normalizeKey = (value: string): string => value.trim().toLowerCase();

const isValidEmail = (value: string): boolean =>
  EMAIL_PATTERN.test(value.trim());

const normalizeEmails = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

const selectedMemberIds = (
  audience: DatabaseEmailAudienceView | null,
): string[] => {
  if (!audience?.recipients) return [];
  // Include former team members — they stay selected until an admin removes them.
  return audience.recipients
    .filter(
      (recipient) =>
        recipient.recipient_type === 'team_member' && recipient.source_user_id,
    )
    .map((recipient) => recipient.source_user_id as string);
};

const recipientLabel = (
  id: string,
  userOpts: Array<{ label: string; value: string }> | undefined,
  audience: DatabaseEmailAudienceView | null,
): string => {
  const opt = (userOpts || []).find((user) => user.value === id);
  if (opt?.label) return opt.label;
  const saved = audience?.recipients?.find(
    (recipient) => recipient.source_user_id === id,
  );
  return saved?.email || id;
};

const selectedExternalEmails = (
  audience: DatabaseEmailAudienceView | null,
): string[] => {
  if (!audience?.recipients) return [];
  return audience.recipients
    .filter((recipient) => recipient.recipient_type === 'external')
    .map((recipient) => recipient.email);
};

const sameIdList = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((id) => right.includes(id));

const sameEmailList = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const rightKeys = new Set(right.map(normalizeKey));
  return left.every((email) => rightKeys.has(normalizeKey(email)));
};

const DatabaseEmailAudienceEditor: React.FC<
  DatabaseEmailAudienceEditorProps
> = ({ databaseId, teamMembers, variant = 'entity' }) => {
  const teamExtras = variant === 'product';
  const { userOpts: contextUserOpts } = useContext(GlobalContext);
  const userOpts = teamMembers || contextUserOpts;
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const [audience, setAudience] = useState<DatabaseEmailAudienceView | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [draftMode, setDraftMode] = useState<'all_team' | 'selected'>(
    'all_team',
  );
  const [draftMemberIds, setDraftMemberIds] = useState<string[]>([]);
  const [draftExternalEmails, setDraftExternalEmails] = useState<string[]>([]);
  const [externalDraft, setExternalDraft] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [confirmAllTeamOpen, setConfirmAllTeamOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const applyAudience = (next: DatabaseEmailAudienceView) => {
    setAudience(next);
    setDraftMode(next.mode);
    setDraftMemberIds(selectedMemberIds(next));
    setDraftExternalEmails(selectedExternalEmails(next));
    setExternalDraft('');
    setFieldError(null);
    setConflictMessage(null);
    setConfirmAllTeamOpen(false);
  };

  const switchToAllTeam = () => {
    setDraftMode('all_team');
    setFieldError(null);
    setConflictMessage(null);
    setConfirmAllTeamOpen(false);
  };

  const requestAllTeamMode = () => {
    if (draftMode === 'selected') {
      setConfirmAllTeamOpen(true);
      return;
    }
    switchToAllTeam();
  };

  const loadAudience = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await BiteDBAPI.getDatabaseEmailAudience(databaseId);
      if (response?.success && response.result) {
        applyAudience(response.result as DatabaseEmailAudienceView);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    BiteDBAPI.getDatabaseEmailAudience(databaseId)
      .then((response) => {
        if (!active) return;
        if (response?.success && response.result) {
          applyAudience(response.result as DatabaseEmailAudienceView);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [databaseId]);

  // On product databases the all-team audience ignores any team members left
  // over from a selected list; they don't count or get saved.
  const activeMemberIds =
    draftMode === 'selected' || !teamExtras ? draftMemberIds : [];

  const teamEmailKeys = new Set(
    activeMemberIds
      .map((id) => {
        const opt = (userOpts || []).find((user: any) => user.value === id);
        if (opt?.label) return normalizeKey(String(opt.label));
        const saved = audience?.recipients?.find(
          (recipient) => recipient.source_user_id === id,
        );
        return saved?.email ? normalizeKey(saved.email) : '';
      })
      .filter(Boolean),
  );

  const allTeamEmails = Array.from(
    new Map(
      (userOpts || [])
        .map((user: any) => String(user.label || '').trim())
        .filter(Boolean)
        .map((email: string) => [normalizeKey(email), email] as const),
    ).values(),
  );
  const allTeamKeys = new Set(allTeamEmails.map(normalizeKey));
  // On a product database's all-team audience, an additional email that now
  // belongs to a team member is already covered by the team: hide it, leave it
  // out of the count, and drop it on the next save.
  const hidesTeamExtras = (mode: 'all_team' | 'selected') =>
    teamExtras && mode === 'all_team';
  const visibleExternalEmails = hidesTeamExtras(draftMode)
    ? draftExternalEmails.filter(
        (email) => !allTeamKeys.has(normalizeKey(email)),
      )
    : draftExternalEmails;

  const uniqueExternalCount = visibleExternalEmails.filter(
    (email) => !teamEmailKeys.has(normalizeKey(email)),
  ).length;
  const combinedCount = activeMemberIds.length + uniqueExternalCount;

  // Who the draft would email, for the product variant's admin summary. The
  // all-team list comes from the team loaded in the browser.
  const draftTeamEmails =
    draftMode === 'all_team'
      ? allTeamEmails
      : activeMemberIds.map((id) => recipientLabel(id, userOpts, audience));
  const draftTeamKeys = new Set(draftTeamEmails.map(normalizeKey));
  const draftExtraEmails = draftExternalEmails.filter(
    (email) => !draftTeamKeys.has(normalizeKey(email)),
  );

  const baselineMemberIds = selectedMemberIds(audience);
  const baselineExternalEmails =
    audience && hidesTeamExtras(audience.mode)
      ? selectedExternalEmails(audience).filter(
          (email) => !allTeamKeys.has(normalizeKey(email)),
        )
      : selectedExternalEmails(audience);
  const dirty =
    !!audience?.can_manage &&
    (draftMode !== audience.mode ||
      !sameIdList(activeMemberIds, baselineMemberIds) ||
      !sameEmailList(visibleExternalEmails, baselineExternalEmails) ||
      externalDraft.trim() !== '');

  const formerRecipients = (audience?.recipients || []).filter(
    (recipient) =>
      recipient.recipient_type === 'team_member' &&
      !!recipient.source_user_id &&
      !recipient.is_current_team_member &&
      draftMemberIds.includes(recipient.source_user_id),
  );

  const removeFormerRecipient = (sourceUserId: string) => {
    setDraftMemberIds((current) => current.filter((id) => id !== sourceUserId));
    setFieldError(null);
  };

  const appendExternalEmails = (rawValue: string) => {
    const parsed = normalizeEmails(rawValue);
    if (!parsed.length) {
      emailInputRef.current?.focus();
      return;
    }

    const invalid = parsed.find((email) => !isValidEmail(email));
    if (invalid) {
      setFieldError(`Invalid external email address: ${invalid}`);
      return;
    }

    const existing = new Set(draftExternalEmails.map(normalizeKey));
    const next: string[] = [];
    for (const email of parsed) {
      const key = normalizeKey(email);
      if (existing.has(key)) continue;
      existing.add(key);
      next.push(email);
    }

    const nextCombined =
      activeMemberIds.length +
      [...visibleExternalEmails, ...next].filter(
        (email) => !teamEmailKeys.has(normalizeKey(email)),
      ).length;
    if (nextCombined > MAX_RECIPIENTS) {
      setFieldError(
        `Email Audience is limited to ${MAX_RECIPIENTS} recipients`,
      );
      return;
    }

    setDraftExternalEmails((prev) => [...prev, ...next]);
    setExternalDraft('');
    setFieldError(null);
    emailInputRef.current?.focus();
  };

  const handleRemoveExternal = (email: string) => {
    const key = normalizeKey(email);
    setDraftExternalEmails((prev) =>
      prev.filter((item) => normalizeKey(item) !== key),
    );
    setFieldError(null);
  };

  const handleExternalKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      appendExternalEmails(externalDraft);
      return;
    }
    if (
      event.key === 'Backspace' &&
      externalDraft === '' &&
      visibleExternalEmails.length > 0
    ) {
      handleRemoveExternal(
        visibleExternalEmails[visibleExternalEmails.length - 1],
      );
    }
  };

  const handleSave = async () => {
    if (!audience || saving) return;

    const savesExternals = draftMode === 'selected' || teamExtras;
    let externalsToSave = visibleExternalEmails;
    if (savesExternals && externalDraft.trim()) {
      const parsed = normalizeEmails(externalDraft);
      const invalid = parsed.find((email) => !isValidEmail(email));
      if (invalid) {
        setFieldError(`Invalid external email address: ${invalid}`);
        return;
      }
      const existing = new Set(visibleExternalEmails.map(normalizeKey));
      const pending = parsed.filter(
        (email) => !existing.has(normalizeKey(email)),
      );
      externalsToSave = [...visibleExternalEmails, ...pending];
    }

    if (savesExternals) {
      const uniqueExternals = externalsToSave.filter(
        (email) => !teamEmailKeys.has(normalizeKey(email)),
      ).length;
      if (activeMemberIds.length + uniqueExternals > MAX_RECIPIENTS) {
        setFieldError(
          `Email Audience is limited to ${MAX_RECIPIENTS} recipients`,
        );
        return;
      }
    }

    setSaving(true);
    setFieldError(null);
    setConflictMessage(null);
    try {
      const response = await BiteDBAPI.saveDatabaseEmailAudience(databaseId, {
        expected_version: audience.version,
        mode: draftMode,
        team_member_ids: draftMode === 'selected' ? draftMemberIds : [],
        external_emails: savesExternals ? externalsToSave : [],
      });
      if (response.success && response.result) {
        applyAudience(response.result as DatabaseEmailAudienceView);
        enqueueSnackbar('Email Audience saved', { variant: 'success' });
        return;
      }
      if (response.status === 409) {
        setConflictMessage(
          'This Email Audience was updated by another administrator. Reload the latest audience before saving again.',
        );
        enqueueSnackbar('Email Audience was updated elsewhere.', {
          variant: 'warning',
        });
        return;
      }
      const message =
        typeof response.result === 'string'
          ? response.result
          : 'Failed to save Email Audience';
      setFieldError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } catch (err) {
      enqueueSnackbar(`Failed to save Email Audience: ${err}`, {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Breakdown line, View/Hide toggle, and read-only list of who is emailed.
  const renderRecipientBreakdown = (
    teamEmails: string[],
    otherEmails: string[],
    otherWord: 'additional' | 'external',
  ) => (
    <>
      <div className='hit-meta'>
        {teamEmails.length} team{' '}
        {teamEmails.length === 1 ? 'member' : 'members'} + {otherEmails.length}{' '}
        {otherWord} {otherEmails.length === 1 ? 'email' : 'emails'}
      </div>
      <button
        type='button'
        className='btn-outline database-email-audience-view-toggle'
        aria-expanded={showRecipients}
        onClick={() => setShowRecipients((open) => !open)}
      >
        {showRecipients ? 'Hide recipients' : 'View recipients'}
      </button>
      {showRecipients &&
        (teamEmails.length + otherEmails.length > 0 ? (
          <div className='team-members-chip-list'>
            {[...teamEmails, ...otherEmails].map((email) => (
              <div key={normalizeKey(email)} className='team-member-chip'>
                <div className='team-member-chip-text'>{email}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className='hit-meta'>No recipients.</div>
        ))}
    </>
  );

  const renderExternalField = (label: string, count?: string) => (
    <div className='database-email-audience-field'>
      {count ? (
        <div className='database-email-audience-count-row'>
          <span className='database-email-audience-field-label'>{label}</span>
          <span className='database-email-audience-count'>{count}</span>
        </div>
      ) : (
        <div className='database-email-audience-field-label'>{label}</div>
      )}
      <div
        className='team-members-chip-input invite-user-modal-chip-input database-email-audience-chip-input'
        onClick={() => emailInputRef.current?.focus()}
      >
        <div className='team-members-chip-list'>
          {visibleExternalEmails.map((email, index) => (
            <div
              key={`external-email-${normalizeKey(email)}-${index}`}
              className='team-member-chip'
            >
              <div className='team-member-chip-text'>{email}</div>
              <button
                type='button'
                className='team-member-remove-btn'
                onClick={() => handleRemoveExternal(email)}
                aria-label={`Remove external email ${email}`}
              >
                x
              </button>
            </div>
          ))}
          <input
            ref={emailInputRef}
            id='email-audience-external'
            placeholder={
              visibleExternalEmails.length === 0 ? 'email@example.com' : ''
            }
            type='text'
            className='team-members-chip-input-field'
            name='externalEmail'
            value={externalDraft}
            onChange={(event) => {
              setExternalDraft(event.target.value);
              if (fieldError) setFieldError(null);
            }}
            onKeyDown={handleExternalKeyDown}
            onBlur={() => {
              if (externalDraft.trim()) {
                appendExternalEmails(externalDraft);
              }
            }}
          />
        </div>
      </div>
      <div className='database-email-audience-external-actions'>
        {/* Import Hub adds addresses with Enter or comma only. */}
        {!teamExtras && (
          <div
            className='team-members-add-btn'
            onClick={() => appendExternalEmails(externalDraft)}
          >
            + Add external email
          </div>
        )}
        <div className='team-members-helper-text'>
          {teamExtras
            ? 'Press Enter or comma to append addresses. No invitation or confirmation is sent.'
            : 'Press Enter, comma, or Add to append addresses. No invitation or confirmation is sent.'}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div
        className='database-email-audience-card'
        aria-label='Loading email audience'
      >
        <Skeleton
          variant='text'
          width='38%'
          sx={{ bgcolor: 'var(--bg-surface)', fontSize: '1.1rem' }}
        />
        <Skeleton
          variant='rounded'
          height={72}
          sx={{ bgcolor: 'var(--bg-surface)' }}
        />
      </div>
    );
  }

  if (error || !audience) {
    return (
      <section
        className='database-email-audience-card'
        aria-labelledby='email-audience-heading'
      >
        <div className='hit-title' id='email-audience-heading'>
          <GroupsOutlinedIcon sx={{ fontSize: 14 }} /> Email Audience
        </div>
        <div className='hit-meta'>
          Email audience is temporarily unavailable.
        </div>
      </section>
    );
  }

  if (!audience.can_manage) {
    // All-team audiences are emailed to the current team (as loaded in the
    // browser); selected audiences to their saved team-member snapshots.
    const savedRecipients = audience.recipients || [];
    const viewTeamEmails =
      audience.mode === 'all_team'
        ? Array.from(
            new Map(
              (userOpts || [])
                .map((user: any) => String(user.label || '').trim())
                .filter(Boolean)
                .map((email: string) => [normalizeKey(email), email] as const),
            ).values(),
          )
        : savedRecipients
            .filter((recipient) => recipient.recipient_type === 'team_member')
            .map((recipient) => recipient.email);
    const viewTeamKeys = new Set(viewTeamEmails.map(normalizeKey));
    const viewOtherEmails = savedRecipients
      .filter((recipient) => recipient.recipient_type === 'external')
      .map((recipient) => recipient.email)
      .filter((email) => !viewTeamKeys.has(normalizeKey(email)));
    return (
      <section
        className='database-email-audience-card'
        aria-labelledby='email-audience-heading'
      >
        <div className='hit-title' id='email-audience-heading'>
          <GroupsOutlinedIcon sx={{ fontSize: 14 }} /> Email Audience
        </div>
        <div className='database-email-audience-summary'>
          <div>
            <strong>
              {audience.mode === 'all_team'
                ? 'All current team members'
                : 'Selected recipients'}
            </strong>
            <div className='hit-meta'>
              {audience.mode === 'all_team'
                ? 'Updated automatically as team membership changes.'
                : 'Uses the saved recipient list for this database.'}
            </div>
          </div>
          <span className='database-email-audience-count'>
            {audience.recipient_count}{' '}
            {audience.recipient_count === 1 ? 'recipient' : 'recipients'}
          </span>
        </div>
        {audience.recipients && (
          <>
            <div className='database-email-audience-total'>
              {renderRecipientBreakdown(
                viewTeamEmails,
                viewOtherEmails,
                audience.mode === 'all_team' ? 'additional' : 'external',
              )}
            </div>
            <div className='hit-meta database-email-audience-readonly-note'>
              Only team admins can change this list.
            </div>
          </>
        )}
      </section>
    );
  }

  return (
    <section
      className='database-email-audience-card'
      aria-labelledby='email-audience-heading'
    >
      <Modal
        open={confirmAllTeamOpen}
        onClose={() => setConfirmAllTeamOpen(false)}
      >
        <div className='database-email-audience-confirm-modal'>
          <div className='hit-title'>Discard selected recipients?</div>
          <div className='hit-meta'>
            {teamExtras
              ? 'Switching to all current team members discards the selected team members. External emails stay as extra recipients.'
              : 'Switching to all current team members discards the saved recipient list for this database.'}
          </div>
          <div className='database-email-audience-confirm-actions'>
            <button
              type='button'
              className='btn-outline'
              onClick={() => setConfirmAllTeamOpen(false)}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn-primary'
              onClick={switchToAllTeam}
            >
              Discard and switch
            </button>
          </div>
        </div>
      </Modal>

      <div className='hit-title' id='email-audience-heading'>
        <GroupsOutlinedIcon sx={{ fontSize: 14 }} /> Email Audience
      </div>
      <div className='hit-meta'>
        {teamExtras
          ? 'Choose who receives screening completion, tariff change, and Daily Updates emails for this database.'
          : 'Choose who receives screening completion emails and Daily Updates for this database.'}
      </div>

      <div className='database-email-audience-mode-row' role='radiogroup'>
        <label className='database-email-audience-mode-option'>
          <input
            type='radio'
            name='email-audience-mode'
            checked={draftMode === 'all_team'}
            onChange={requestAllTeamMode}
          />
          <span className='database-email-audience-mode-copy'>
            <strong>All current team members</strong>
            <span className='hit-meta'>
              {teamExtras
                ? 'Updated automatically as team membership changes. You can add extra emails of those not on the team.'
                : 'Updated automatically as team membership changes.'}
            </span>
          </span>
        </label>
        <label className='database-email-audience-mode-option'>
          <input
            type='radio'
            name='email-audience-mode'
            checked={draftMode === 'selected'}
            onChange={() => {
              setDraftMode('selected');
              setFieldError(null);
              setConflictMessage(null);
            }}
          />
          <span className='database-email-audience-mode-copy'>
            <strong>Selected recipients</strong>
            <span className='hit-meta'>
              Save a custom fixed list of emails.
            </span>
          </span>
        </label>
      </div>

      {teamExtras && (
        <div className='database-email-audience-total'>
          <div className='database-email-audience-count-row'>
            <span className='database-email-audience-field-label'>
              Total recipients
            </span>
            <span className='database-email-audience-count'>
              {draftTeamEmails.length + draftExtraEmails.length}
            </span>
          </div>
          {renderRecipientBreakdown(
            draftTeamEmails,
            draftExtraEmails,
            draftMode === 'all_team' ? 'additional' : 'external',
          )}
        </div>
      )}

      {draftMode === 'all_team' && teamExtras && (
        <div className='database-email-audience-picker'>
          {renderExternalField(
            'Additional email recipients',
            `${visibleExternalEmails.length} / ${MAX_RECIPIENTS}`,
          )}
        </div>
      )}

      {draftMode === 'selected' && (
        <div className='database-email-audience-picker'>
          <div className='database-email-audience-count-row'>
            <span className='database-email-audience-field-label'>
              Combined recipients
            </span>
            <span className='database-email-audience-count'>
              {combinedCount} / {MAX_RECIPIENTS}
            </span>
          </div>

          <div className='database-email-audience-field'>
            <div className='database-email-audience-field-label'>
              Team members
            </div>
            <FormControl fullWidth>
              <CustomSelect
                multiple
                displayEmpty
                value={draftMemberIds.filter((id) =>
                  (userOpts || []).some((user: any) => user.value === id),
                )}
                onChange={(event: any) => {
                  const picked =
                    typeof event.target.value === 'string'
                      ? event.target.value.split(',')
                      : event.target.value;
                  const formerIds = draftMemberIds.filter(
                    (id) =>
                      !(userOpts || []).some((user: any) => user.value === id),
                  );
                  const value = Array.from(new Set([...picked, ...formerIds]));
                  const nextTeamKeys = new Set(
                    value
                      .map((id: string) => {
                        const opt = (userOpts || []).find(
                          (user: any) => user.value === id,
                        );
                        if (opt?.label) return normalizeKey(String(opt.label));
                        const saved = audience?.recipients?.find(
                          (recipient) => recipient.source_user_id === id,
                        );
                        return saved?.email ? normalizeKey(saved.email) : '';
                      })
                      .filter(Boolean),
                  );
                  const nextCount =
                    value.length +
                    draftExternalEmails.filter(
                      (email) => !nextTeamKeys.has(normalizeKey(email)),
                    ).length;
                  if (nextCount > MAX_RECIPIENTS) {
                    setFieldError(
                      `Email Audience is limited to ${MAX_RECIPIENTS} recipients`,
                    );
                    return;
                  }
                  setDraftMemberIds(value);
                  setFieldError(null);
                }}
                input={<OutlinedInput />}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={() => {
                  if (!draftMemberIds.length) {
                    return (
                      <span className='hit-meta'>Select team members</span>
                    );
                  }
                  return draftMemberIds
                    .map((id) => recipientLabel(id, userOpts, audience))
                    .join(', ');
                }}
                inputProps={{
                  MenuProps: {
                    MenuListProps: {
                      sx: {
                        backgroundColor: 'var(--bg-colors)',
                        color: 'var(--Text-Main, var(--text-primary))',
                      },
                    },
                  },
                }}
              >
                {(userOpts || []).map((user: any) => (
                  <MenuItem key={user.value} value={user.value}>
                    <Checkbox
                      checked={draftMemberIds.indexOf(user.value) > -1}
                    />
                    {user.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </div>

          {formerRecipients.length > 0 && (
            <ul className='database-email-audience-former-list'>
              {formerRecipients.map((recipient) => (
                <li
                  key={recipient.source_user_id || recipient.id}
                  className='database-email-audience-former-row'
                >
                  <div className='database-email-audience-former-meta'>
                    <span>{recipient.email}</span>
                    <span className='screening-badge lens-chip chip-yellow'>
                      Former team member
                    </span>
                  </div>
                  <button
                    type='button'
                    className='btn-outline'
                    onClick={() =>
                      removeFormerRecipient(recipient.source_user_id as string)
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {renderExternalField('External recipients')}
        </div>
      )}

      {conflictMessage && (
        <div className='database-email-audience-conflict' role='alert'>
          <div className='hit-meta'>{conflictMessage}</div>
          <button
            type='button'
            className='btn-outline'
            onClick={() => {
              loadAudience().catch(() => {
                setError(true);
              });
            }}
          >
            Reload Email Audience
          </button>
        </div>
      )}

      {fieldError && (
        <div className='database-email-audience-error' role='alert'>
          {fieldError}
        </div>
      )}

      <div className='database-email-audience-actions'>
        <button
          type='button'
          className='btn-primary'
          disabled={!dirty || saving || !!conflictMessage}
          onClick={handleSave}
          style={{
            opacity: !dirty || saving || !!conflictMessage ? 0.6 : 1,
            cursor:
              !dirty || saving || !!conflictMessage ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: 'inherit' }} />
          ) : (
            'Save Email Audience'
          )}
        </button>
      </div>
    </section>
  );
};

export default DatabaseEmailAudienceEditor;
